import { useQuery, UseQueryOptions } from "@tanstack/react-query";
import get from "lodash/get";
import { PaginatedResponse } from "..";
import { PaginatedQueryParams } from "../types";
import type {
  PaginatedQueryFunction,
  UsePaginatedQueryFunction,
} from "./queries";
import {
  fetchPaginatedQueryFunction,
  fetchQueryFunction,
  makeQueryKey,
  usePaginatedQuery,
} from "./queries";

type Size<T extends Record<string, any> = {}> = T & {
  url: string;
  size: number;
  filename: string;
  mime_type: string;
  [key: string]: any;
};

type BaseMedia<T extends Record<string, any> = {}> = {
  id: number;
  caption?: string;
  thumbhash?: string;
  mime_type: string;
  sizes: { original: Size<T> };
  outbound_url?: string;
};

type ImageMetadata = {
  width: number;
  height: number;
};

type VideoMetadata = {
  width: number;
  height: number;
};

export type ImageThumbnails = {
  original: Size<ImageMetadata>;
  xlarge: Size<ImageMetadata>;
  large: Size<ImageMetadata>;
  medium: Size<ImageMetadata>;
  small: Size<ImageMetadata>;
  thumb: Size<ImageMetadata>;
  thumb_square: Size<ImageMetadata>;
};

export type MediaImageSize = keyof ImageThumbnails;

export type VideoSizes = {
  original: Size<VideoMetadata>;
  xlarge: Size<VideoMetadata>;
  large: Size<VideoMetadata>;
  medium: Size<VideoMetadata>;
  small: Size<VideoMetadata>;
};

export type MediaVideoSize = keyof VideoSizes;

export type VideoMedia = BaseMedia<VideoSizes> & {
  type: "Video";
  sizes: VideoSizes;
};

export type ImageMedia = BaseMedia & {
  type: "Image";
  sizes: ImageThumbnails;
};

export type AudioMedia = BaseMedia<{ duration: number }> & {
  type: "Audio";
};
export type PDFMedia = BaseMedia & {
  type: "Pdf";
  sizes: {
    original: Size<{ page_count: number }>;
    thumbnail: Size<ImageMetadata>;
  };
};
export type OtherMedia = BaseMedia & {
  type: "OtherFile";
};
export type Media =
  | VideoMedia
  | ImageMedia
  | AudioMedia
  | PDFMedia
  | OtherMedia;

/** Simplified image reference for headers/cards (`url` + optional caption). */
export type ImageRef = {
  url: string;
  caption?: string;
  mime?: string;
  thumbnail_url?: string;
};

export const getImage = (
  media: Media | undefined | null,
  size: keyof ImageThumbnails = "xlarge",
) => {
  if (!media) return null;
  switch (media.type) {
    case "Image":
      return get(media.sizes, size);
    case "Video":
      return get(media.sizes, ["thumbnails", size]);
    case "Pdf":
      return get(media.sizes, "thumbnail");
    default:
      return null;
  }
};

export const getVideo = (
  media: Media | undefined | null,
  size: keyof VideoSizes = "xlarge",
) => {
  if (!media) return null;
  switch (media.type) {
    case "Video":
      return get(media.sizes, size);
    default:
      return null;
  }
};

// --- V2 Media API (GET /api/v2/media) ---

/** Max IDs per batch request; API drops extras and sets truncated. */
export const MEDIA_BATCH_MAX_IDS = 100;

/** Response when requesting media by filter[id][]; no pagination. */
export type MediaBatchResponse = {
  records: Media[];
  truncated?: boolean;
};

/** Params for paginated media list (no filter[id][]). */
export type MediaListParams = PaginatedQueryParams;

type MediaListResponse = PaginatedResponse<Media>;

/** Single media by ID: GET /api/v2/media/:id */
export const fetchMedia = (id?: number | string | null) =>
  fetchQueryFunction<void, Media>({
    path: `media/${id}`,
    resource: "media",
    id: id ?? undefined,
  });

export const useMedia = (
  id?: number | string | null,
  options?: Omit<UseQueryOptions<Media, Error>, "queryKey" | "queryFn">,
) =>
  useQuery<Media, Error>({
    queryKey: makeQueryKey({ resource: "media", id }),
    queryFn: () => fetchMedia(id),
    ...options,
    enabled: id != null && id !== "" && options?.enabled !== false,
  });

/** Batch by IDs: GET /api/v2/media?filter[id][]=1&... (max 100 IDs). */
export const fetchMediaBatch = (ids: (number | string)[]) => {
  const trimmed = ids.slice(0, MEDIA_BATCH_MAX_IDS);
  return fetchQueryFunction<{ filter: { id: number[] } }, MediaBatchResponse>({
    path: "media",
    resource: ["media", "batch"],
    params:
      trimmed.length > 0 ? { filter: { id: trimmed.map(Number) } } : undefined,
  });
};

export const useMediaBatch = (
  ids: (number | string)[],
  options?: Omit<
    UseQueryOptions<MediaBatchResponse, Error>,
    "queryKey" | "queryFn"
  >,
) =>
  useQuery<MediaBatchResponse, Error>({
    queryKey: makeQueryKey({ resource: ["media", "batch"], params: { ids } }),
    queryFn: () => fetchMediaBatch(ids),
    ...options,
    enabled: ids.length > 0 && options?.enabled !== false,
  });

/** Paginated list: GET /api/v2/media?page=1&per_page=15 (no filter[id][]). */
export const fetchMediaList: PaginatedQueryFunction<
  MediaListParams,
  MediaListResponse
> = (params, preload) =>
  fetchPaginatedQueryFunction({
    path: "media",
    resource: "media",
    params,
    preload,
  });

export const useMediaList: UsePaginatedQueryFunction<Media, MediaListParams> = (
  params,
  options,
) => usePaginatedQuery("media", fetchMediaList, params, options);
